// ═══════════════════════════════════════════════════════════════
// V\u00B7IANet Governance \u2014 Ficha de Caracterizaci\u00F3n
// Process tree sidebar + Ficha (colapsable) + Visor (expande)
// Standalone panel: no own header, auto-selects PE.21
// ═══════════════════════════════════════════════════════════════

import {
  useState,
  useEffect,
  useCallback,
  useMemo,
  useRef,
  Component,
  type ReactNode,
} from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { C, TIPOLOGIA_CONFIG } from '../../../../../lib/design';
import { Ic, ICON_PATHS, StatusBadge } from '../../../../../lib/ui';
import { useI18n } from '../../../../../i18n';
import { LoadingSpinner } from '../../../../shared/components/LoadingSpinner';
import { fetchProcesos, fetchPendientes, fetchDocumentos } from '../../../../shared/services/api';
import type { Proceso, DocumentoResumen, TipologiaProceso } from '../../../../../types';
import { ProcessSheetPanel } from '../../components/ProcessSheetPanel';
import { DocumentPanel } from '../components/DocumentPanel';
import type { PanelState } from '../components/DocumentPanel';
import { exportFichaToPdf } from '../../../../../lib/exportPdf';
import FlowEditor from '../../flow/editor/FlowEditor';

// \u2500\u2500\u2500 Types \u2500\u2500\u2500
interface GroupedProcess {
  tipologia: TipologiaProceso;
  groups: { name: string; procesos: Proceso[] }[];
}

// \u2500\u2500\u2500 Constants \u2500\u2500\u2500
const SIDEBAR_W = 260;
const hdrBtn: React.CSSProperties = {
  display: 'inline-flex',
  alignItems: 'center',
  gap: 4,
  padding: '4px 10px',
  fontSize: 11,
  fontWeight: 500,
  fontFamily: 'inherit',
  color: C.textSec,
  backgroundColor: 'transparent',
  border: `1px solid ${C.border}`,
  borderRadius: 5,
  cursor: 'pointer',
  whiteSpace: 'nowrap',
  transition: 'background 0.15s',
};

// ═══════════════════════════════════════════════════════════════
// PROCESS TREE SIDEBAR \u2014 Ficha variant (no explorer button)
// ═══════════════════════════════════════════════════════════════
type ViewMode = 'full' | 'docs' | 'hidden' | 'flow';

const VIEW_MODES: { key: ViewMode; label: string }[] = [
  { key: 'full', label: 'Ficha' },
  { key: 'docs', label: 'Docs' },
  { key: 'hidden', label: 'Visor' },
  { key: 'flow', label: 'Flow' },
];

// --- Safe Flow Editor (pre-checks API + ErrorBoundary) ---
class FlowErrorBoundary extends Component<{ children: ReactNode }, { hasError: boolean }> {
  state = { hasError: false };
  static getDerivedStateFromError() {
    return { hasError: true };
  }
  render() {
    if (this.state.hasError) {
      return (
        <div
          style={{
            flex: 1,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            padding: 32,
          }}
        >
          <div style={{ textAlign: 'center', color: 'rgba(0,0,0,0.45)' }}>
            <div style={{ fontSize: 48, marginBottom: 16 }}>{'\u26D3'}</div>
            <div style={{ fontSize: 16, fontWeight: 500 }}>Error en el editor</div>
            <button
              onClick={() => this.setState({ hasError: false })}
              style={{
                marginTop: 16,
                padding: '8px 20px',
                borderRadius: 8,
                border: '1px solid #d5d5d5',
                background: '#fff',
                cursor: 'pointer',
                fontSize: 13,
                fontFamily: 'inherit',
              }}
            >
              Reintentar
            </button>
          </div>
        </div>
      );
    }
    return this.props.children;
  }
}

// SafeFlowEditor removed (prompt 261) — FlowEditor handles its own loading/error/empty states via useFlowData

function FichaTreeSidebar({
  selectedCodigo,
  onSelectProceso,
  onNavigateDoc,
  onProcesosLoaded,
  viewMode,
  onViewModeChange,
}: {
  selectedCodigo: string | null;
  onSelectProceso: (p: Proceso) => void;
  // 0661.8: pasamos el doc completo (no solo codigo) para que el padre
  // pueda resolver proceso + setear modo Visor sin navegar fuera.
  onNavigateDoc: (doc: DocumentoResumen) => void;
  onProcesosLoaded?: (procesos: Proceso[]) => void;
  viewMode: ViewMode;
  onViewModeChange: (mode: ViewMode) => void;
}) {
  const { t, lang } = useI18n();
  const [procesos, setProcesos] = useState<Proceso[]>([]);
  const [loading, setLoading] = useState(true);
  const [expandedTipo, setExpandedTipo] = useState<Record<string, boolean>>({
    PE: true,
    PO: true,
    PS: true,
  });
  const [expandedGroup, setExpandedGroup] = useState<Record<string, boolean>>({});
  const [pendientes, setPendientes] = useState<DocumentoResumen[]>([]);
  const [pendientesExpanded, setPendientesExpanded] = useState(true);

  useEffect(() => {
    fetchProcesos()
      .then((r) => {
        setProcesos(r.data);
        onProcesosLoaded?.(r.data);
      })
      .catch(() => {})
      .finally(() => setLoading(false));
    fetchPendientes()
      .then((r) => setPendientes(r.datos ?? []))
      .catch(() => {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [lang]);

  const enRevision = pendientes.filter((d) => d.estado === 'EN_REVISION');
  const enAprobacion = pendientes.filter((d) => d.estado === 'APROBADO');
  const totalPendientes = enRevision.length + enAprobacion.length;

  const grouped: GroupedProcess[] = useMemo(
    () =>
      (['PE', 'PO', 'PS'] as TipologiaProceso[]).map((tip) => {
        const tipProcs = procesos.filter((p) => p.tipologia === tip);
        const groupMap: Record<string, Proceso[]> = {};
        tipProcs.forEach((p) => {
          const g = p.grupoFuncional || 'Sin grupo';
          if (!groupMap[g]) groupMap[g] = [];
          groupMap[g].push(p);
        });
        return {
          tipologia: tip,
          groups: Object.entries(groupMap).map(([name, procs]) => ({
            name,
            procesos: procs.sort((a, b) => a.codigo.localeCompare(b.codigo)),
          })),
        };
      }),
    [procesos],
  );

  const toggleAll = useCallback(
    (expand: boolean) => {
      const tipo: Record<string, boolean> = {};
      const grp: Record<string, boolean> = {};
      (['PE', 'PO', 'PS'] as TipologiaProceso[]).forEach((t) => {
        tipo[t] = expand;
      });
      grouped.forEach((g) => {
        g.groups.forEach((gr) => {
          grp[`${g.tipologia}-${gr.name}`] = expand;
        });
      });
      setExpandedTipo(tipo);
      setExpandedGroup(grp);
    },
    [grouped],
  );

  return (
    <div
      style={{
        width: SIDEBAR_W,
        minWidth: SIDEBAR_W,
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
        backgroundColor: '#fff',
        color: C.text,
        fontFamily: 'inherit',
        overflow: 'hidden',
        border: '1px solid #e8e8e8',
        borderRadius: 12,
        boxShadow: '0 1px 3px rgba(0,0,0,0.08)',
      }}
    >
      {/* View mode tabs — pill style */}
      <div
        style={{
          display: 'flex',
          gap: 3,
          padding: '6px 8px',
          borderBottom: '1px solid #e8e8e8',
          flexShrink: 0,
        }}
      >
        {VIEW_MODES.map((m) => (
          <button
            key={m.key}
            onClick={() => onViewModeChange(m.key)}
            style={
              viewMode === m.key
                ? {
                    padding: '7px 0',
                    borderRadius: 8,
                    fontSize: 12,
                    fontWeight: 600,
                    border: 'none',
                    cursor: 'pointer',
                    fontFamily: 'inherit',
                    background: '#1F3864',
                    color: '#fff',
                    transition: 'all 0.2s',
                    flex: 1,
                  }
                : {
                    padding: '7px 0',
                    borderRadius: 8,
                    fontSize: 12,
                    fontWeight: 500,
                    border: 'none',
                    cursor: 'pointer',
                    fontFamily: 'inherit',
                    background: 'transparent',
                    color: '#98A2B3',
                    transition: 'all 0.2s',
                    flex: 1,
                  }
            }
          >
            {m.label}
          </button>
        ))}
      </div>

      {/* MAPA DE PROCESOS label */}
      <div style={{ padding: '10px 18px 2px' }}>
        <span
          style={{
            fontSize: 10,
            fontWeight: 700,
            color: C.textMut,
            letterSpacing: 1.5,
            textTransform: 'uppercase',
          }}
        >
          {t.workspace.mapaProcesos}
        </span>
      </div>
      {/* Expand/collapse buttons */}
      <div style={{ display: 'flex', gap: 4, padding: '0 18px 6px' }}>
        <button
          onClick={() => toggleAll(true)}
          style={{
            width: 24,
            height: 22,
            borderRadius: 5,
            border: '1px solid #EAECF0',
            background: '#fff',
            cursor: 'pointer',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}
        >
          <svg
            width={10}
            height={10}
            viewBox="0 0 24 24"
            fill="none"
            stroke="#98A2B3"
            strokeWidth="2.5"
          >
            <polyline points="6 9 12 15 18 9" />
          </svg>
        </button>
        <button
          onClick={() => toggleAll(false)}
          style={{
            width: 24,
            height: 22,
            borderRadius: 5,
            border: '1px solid #EAECF0',
            background: '#fff',
            cursor: 'pointer',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}
        >
          <svg
            width={10}
            height={10}
            viewBox="0 0 24 24"
            fill="none"
            stroke="#98A2B3"
            strokeWidth="2.5"
            style={{ transform: 'rotate(-90deg)' }}
          >
            <polyline points="6 9 12 15 18 9" />
          </svg>
        </button>
      </div>

      {/* Process tree */}
      <div style={{ flex: 1, overflowY: 'auto', overflowX: 'hidden' }}>
        {loading ? (
          <div style={{ padding: '20px 18px', textAlign: 'center' }}>
            <LoadingSpinner text={t.workspace.cargandoProcesos} size="sm" />
          </div>
        ) : (
          grouped.map(({ tipologia, groups }) => {
            const tipCfg = TIPOLOGIA_CONFIG[tipologia];
            const isExpanded = expandedTipo[tipologia] ?? true;
            return (
              <div key={tipologia}>
                <button
                  onClick={() => setExpandedTipo((s) => ({ ...s, [tipologia]: !isExpanded }))}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 8,
                    width: '100%',
                    padding: '8px 10px',
                    border: 'none',
                    background: 'none',
                    borderRadius: 8,
                    cursor: 'pointer',
                    textAlign: 'left',
                    fontFamily: 'inherit',
                    transition: 'background 0.15s',
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.background = '#F5F6F8';
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.background = 'transparent';
                  }}
                >
                  <span style={{ color: tipCfg.color, display: 'flex', flexShrink: 0 }}>
                    {tipologia === 'PE' && (
                      <svg
                        width={15}
                        height={15}
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke="currentColor"
                        strokeWidth="1.8"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      >
                        <path d="M12 2l3.09 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l6.91-1.01z" />
                      </svg>
                    )}
                    {tipologia === 'PO' && (
                      <svg
                        width={15}
                        height={15}
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke="currentColor"
                        strokeWidth="1.8"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      >
                        <circle cx="5" cy="6" r="3" />
                        <circle cx="19" cy="6" r="3" />
                        <circle cx="12" cy="18" r="3" />
                        <path d="M8 6h8M5 9v3l7 3M19 9v3l-7 3" />
                      </svg>
                    )}
                    {tipologia === 'PS' && (
                      <svg
                        width={15}
                        height={15}
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke="currentColor"
                        strokeWidth="1.8"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      >
                        <path d="M14.7 6.3a1 1 0 0 0 0 1.4l1.6 1.6a1 1 0 0 0 1.4 0l3.77-3.77a6 6 0 0 1-7.94 7.94l-6.91 6.91a2.12 2.12 0 0 1-3-3l6.91-6.91a6 6 0 0 1 7.94-7.94l-3.76 3.76z" />
                      </svg>
                    )}
                  </span>
                  <span style={{ fontSize: 12, fontWeight: 600, color: C.text, flex: 1 }}>
                    {tipCfg.label}
                  </span>
                  <svg
                    width={12}
                    height={12}
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="#D0D5DD"
                    strokeWidth="2.5"
                    style={{
                      transform: isExpanded ? 'rotate(0)' : 'rotate(-90deg)',
                      transition: 'transform 0.25s ease',
                      flexShrink: 0,
                    }}
                  >
                    <polyline points="6 9 12 15 18 9" />
                  </svg>
                </button>

                {isExpanded &&
                  groups.map(({ name, procesos: procs }) => {
                    const groupKey = `${tipologia}-${name}`;
                    const isGroupExpanded = expandedGroup[groupKey] ?? false;
                    return (
                      <div key={groupKey}>
                        <button
                          onClick={() =>
                            setExpandedGroup((s) => ({ ...s, [groupKey]: !isGroupExpanded }))
                          }
                          style={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: 6,
                            width: '100%',
                            padding: '5px 18px 5px 30px',
                            border: 'none',
                            background: 'none',
                            cursor: 'pointer',
                            textAlign: 'left',
                            fontFamily: 'inherit',
                            transition: 'color 0.15s',
                          }}
                          onMouseEnter={(e) => {
                            (e.currentTarget.lastElementChild as HTMLElement).style.color = C.azul2;
                          }}
                          onMouseLeave={(e) => {
                            (e.currentTarget.lastElementChild as HTMLElement).style.color =
                              C.textSec;
                          }}
                        >
                          <svg
                            width={10}
                            height={10}
                            viewBox="0 0 24 24"
                            fill="none"
                            stroke="#D0D5DD"
                            strokeWidth="2.5"
                            style={{
                              transform: isGroupExpanded ? 'rotate(0)' : 'rotate(-90deg)',
                              transition: 'transform 0.2s',
                              flexShrink: 0,
                            }}
                          >
                            <polyline points="6 9 12 15 18 9" />
                          </svg>
                          <span
                            style={{
                              fontSize: 11,
                              fontWeight: 500,
                              color: C.textSec,
                              transition: 'color 0.15s',
                            }}
                          >
                            {name.replace(/^Gr\.\d+\s+/, '')}
                          </span>
                        </button>

                        {isGroupExpanded &&
                          procs.map((proc) => {
                            const active = selectedCodigo === proc.codigo;
                            return (
                              <button
                                key={proc.id}
                                onClick={() => onSelectProceso(proc)}
                                onMouseEnter={(e) => {
                                  if (!active)
                                    e.currentTarget.style.background = 'rgba(248,249,252,0.9)';
                                }}
                                onMouseLeave={(e) => {
                                  if (!active) e.currentTarget.style.background = 'transparent';
                                }}
                                style={{
                                  display: 'flex',
                                  alignItems: 'center',
                                  justifyContent: 'space-between',
                                  width: '100%',
                                  padding: '4px 18px 4px 45px',
                                  borderLeft: active
                                    ? '3px solid #33BCE1'
                                    : '3px solid transparent',
                                  borderTop: 'none',
                                  borderRight: 'none',
                                  borderBottom: 'none',
                                  background: active
                                    ? 'linear-gradient(135deg, rgba(51,188,225,0.10), rgba(31,56,100,0.05))'
                                    : 'transparent',
                                  color: active ? C.azul1 : C.textSec,
                                  fontSize: 10,
                                  fontWeight: 400,
                                  transition: 'background 0.15s',
                                  cursor: 'pointer',
                                  fontFamily: 'inherit',
                                  textAlign: 'left',
                                  borderRadius: 7,
                                }}
                              >
                                <span
                                  style={{
                                    overflow: 'hidden',
                                    textOverflow: 'ellipsis',
                                    whiteSpace: 'nowrap',
                                    flex: 1,
                                  }}
                                >
                                  <span
                                    style={{
                                      fontFamily: "'JetBrains Mono', ui-monospace, monospace",
                                      fontSize: 10.5,
                                      fontWeight: 600,
                                      marginRight: 6,
                                      color: active ? C.azul2 : C.text,
                                      minWidth: 38,
                                    }}
                                  >
                                    {proc.codigo}
                                  </span>
                                  <span style={{ opacity: 0.8 }}>{proc.nombre}</span>
                                </span>
                                {(proc._count?.documentos ?? 0) > 0 && (
                                  <span
                                    style={{
                                      fontSize: 10,
                                      padding: '1px 7px',
                                      borderRadius: 10,
                                      background: active
                                        ? 'linear-gradient(135deg, #33BCE1, #2F5496)'
                                        : 'rgba(51,188,225,0.10)',
                                      color: active ? '#fff' : '#33BCE1',
                                      fontWeight: 700,
                                      flexShrink: 0,
                                      marginLeft: 6,
                                      transition: 'all 0.2s',
                                    }}
                                  >
                                    {proc._count!.documentos}
                                  </span>
                                )}
                              </button>
                            );
                          })}
                      </div>
                    );
                  })}
              </div>
            );
          })
        )}
      </div>

      {/* Pendientes section */}
      {totalPendientes > 0 && (
        <div
          style={{
            padding: '8px 12px',
            borderTop: `1px solid ${C.border}`,
            display: 'flex',
            flexDirection: 'column',
            gap: 2,
            flexShrink: 0,
          }}
        >
          <button
            onClick={() => setPendientesExpanded((e) => !e)}
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: 6,
              padding: '4px 6px',
              border: 'none',
              background: 'none',
              cursor: 'pointer',
              fontFamily: 'inherit',
              width: '100%',
            }}
          >
            <span
              style={{
                fontSize: 10,
                fontWeight: 700,
                color: '#D97706',
                letterSpacing: 1.5,
                textTransform: 'uppercase',
              }}
            >
              {t.workspace.pendientes}
            </span>
            <span
              style={{
                fontSize: 9,
                padding: '1px 6px',
                borderRadius: 10,
                backgroundColor: '#FEF3C7',
                color: '#D97706',
                fontWeight: 700,
              }}
            >
              {totalPendientes}
            </span>
            <span style={{ flex: 1 }} />
            <span style={{ fontSize: 10, color: C.textMut }}>
              {pendientesExpanded ? '\u25BC' : '\u25B6'}
            </span>
          </button>

          {pendientesExpanded && (
            <>
              {enRevision.length > 0 && (
                <div style={{ marginTop: 4 }}>
                  <div
                    style={{
                      fontSize: 10,
                      fontWeight: 600,
                      color: '#D97706',
                      padding: '2px 10px',
                      display: 'flex',
                      alignItems: 'center',
                      gap: 6,
                    }}
                  >
                    {t.workspace.enRevision}
                    <span
                      style={{
                        fontSize: 9,
                        padding: '0px 5px',
                        borderRadius: 8,
                        backgroundColor: '#FEF3C7',
                        color: '#D97706',
                        fontWeight: 700,
                      }}
                    >
                      {enRevision.length}
                    </span>
                  </div>
                  {enRevision.map((d) => (
                    <button
                      key={d.id}
                      onClick={() => onNavigateDoc(d)}
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: 6,
                        width: '100%',
                        padding: '4px 10px 4px 18px',
                        border: 'none',
                        borderRadius: 4,
                        backgroundColor: 'transparent',
                        color: C.textSec,
                        fontSize: 11,
                        cursor: 'pointer',
                        fontFamily: 'inherit',
                        textAlign: 'left',
                        transition: 'background 0.15s',
                      }}
                      onMouseEnter={(e) => {
                        e.currentTarget.style.backgroundColor = '#F3F4F6';
                      }}
                      onMouseLeave={(e) => {
                        e.currentTarget.style.backgroundColor = 'transparent';
                      }}
                    >
                      <span
                        style={{
                          fontWeight: 600,
                          fontSize: 10,
                          color: '#D97706',
                          flexShrink: 0,
                          fontFamily: 'ui-monospace, "Cascadia Code", monospace',
                        }}
                      >
                        {d.codigo}
                      </span>
                      <span
                        style={{
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                          whiteSpace: 'nowrap',
                          flex: 1,
                        }}
                      >
                        {d.titulo}
                      </span>
                    </button>
                  ))}
                </div>
              )}
              {enAprobacion.length > 0 && (
                <div style={{ marginTop: 4 }}>
                  <div
                    style={{
                      fontSize: 10,
                      fontWeight: 600,
                      color: C.azul2,
                      padding: '2px 10px',
                      display: 'flex',
                      alignItems: 'center',
                      gap: 6,
                    }}
                  >
                    {t.workspace.enAprobacion}
                    <span
                      style={{
                        fontSize: 9,
                        padding: '0px 5px',
                        borderRadius: 8,
                        backgroundColor: '#EFF3FF',
                        color: C.azul2,
                        fontWeight: 700,
                      }}
                    >
                      {enAprobacion.length}
                    </span>
                  </div>
                  {enAprobacion.map((d) => (
                    <button
                      key={d.id}
                      onClick={() => onNavigateDoc(d)}
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: 6,
                        width: '100%',
                        padding: '4px 10px 4px 18px',
                        border: 'none',
                        borderRadius: 4,
                        backgroundColor: 'transparent',
                        color: C.textSec,
                        fontSize: 11,
                        cursor: 'pointer',
                        fontFamily: 'inherit',
                        textAlign: 'left',
                        transition: 'background 0.15s',
                      }}
                      onMouseEnter={(e) => {
                        e.currentTarget.style.backgroundColor = '#F3F4F6';
                      }}
                      onMouseLeave={(e) => {
                        e.currentTarget.style.backgroundColor = 'transparent';
                      }}
                    >
                      <span
                        style={{
                          fontWeight: 600,
                          fontSize: 10,
                          color: C.azul2,
                          flexShrink: 0,
                          fontFamily: 'ui-monospace, "Cascadia Code", monospace',
                        }}
                      >
                        {d.codigo}
                      </span>
                      <span
                        style={{
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                          whiteSpace: 'nowrap',
                          flex: 1,
                        }}
                      >
                        {d.titulo}
                      </span>
                    </button>
                  ))}
                </div>
              )}
            </>
          )}
        </div>
      )}
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════
// MAIN COMPONENT \u2014 FichaCaracterizacion (no own header, auto-selects PE.21)
// ═══════════════════════════════════════════════════════════════
export function FichaCaracterizacion() {
  const { t } = useI18n();
  const navigate = useNavigate();

  // 0661.6: query params canonicos para deep-linking desde Action Center,
  // NotificationPanel y similar. Ejemplo de URL canonica:
  //   /process?tab=caracterizacion&proceso=PE.21&doc=PE.21.IT.01&view=hidden
  // - proceso: codigo del proceso a auto-seleccionar (override de PE.21 default)
  // - doc:     codigo del documento a auto-seleccionar dentro del proceso
  // - view:    modo de vista inicial ('full' | 'docs' | 'hidden' | 'flow')
  const [searchParams] = useSearchParams();
  const initialProcesoCodigo = searchParams.get('proceso');
  const initialDocCodigo = searchParams.get('doc');
  const initialView = searchParams.get('view') as ViewMode | null;

  // \u2500\u2500\u2500 State \u2500\u2500\u2500
  const [selectedProceso, setSelectedProceso] = useState<Proceso | null>(null);
  const [selectedDocumento, setSelectedDocumento] = useState<DocumentoResumen | null>(null);
  const [fichaEditing, setFichaEditing] = useState(false);
  const [docEditing, setDocEditing] = useState(false);
  const fichaRef = useRef<HTMLDivElement>(null);
  const docExportRef = useRef<(() => Promise<void>) | null>(null);
  const autoSelectedRef = useRef(false);
  const autoSelectedDocRef = useRef(false);

  // \u2500\u2500\u2500 View mode: 'full' | 'docs' | 'hidden' | 'flow' \u2500\u2500\u2500
  // Si llega `view` valido en query params, lo respeta como estado inicial.
  const [fichaMode, setFichaMode] = useState<ViewMode>(() => {
    const valid: ViewMode[] = ['full', 'docs', 'hidden', 'flow'];
    if (initialView && valid.includes(initialView)) return initialView;
    return 'full';
  });
  // fichaMode === 'hidden' = visor-only mode

  // \u2500\u2500\u2500 Panel state from DocumentPanel \u2500\u2500\u2500
  const [, setPanelState] = useState<PanelState | null>(null);

  const handleExportFicha = useCallback(async () => {
    if (!fichaRef.current || !selectedProceso) return;
    await exportFichaToPdf(fichaRef.current, {
      codigo: `${selectedProceso.codigo}.FC.01`,
      nombre: `Ficha de Caracterizacion \u2014 ${selectedProceso.nombre}`,
      proceso: `${selectedProceso.codigo} ${selectedProceso.nombre}`,
    });
  }, [selectedProceso]);

  // \u2500\u2500\u2500 Handlers \u2500\u2500\u2500
  const handleSelectProceso = useCallback((p: Proceso) => {
    setSelectedProceso(p);
    setSelectedDocumento(null);
  }, []);

  // 0661.8: cache local de la lista de procesos para que `handleNavigateDoc`
  // pueda resolver el `Proceso` completo a partir de `doc.proceso.codigo`
  // sin tener que volver a llamar `fetchProcesos`.
  const [procesosList, setProcesosList] = useState<Proceso[]>([]);

  const handleProcesosLoaded = useCallback(
    (procesos: Proceso[]) => {
      setProcesosList(procesos);
      if (autoSelectedRef.current) return;
      autoSelectedRef.current = true;
      // 0661.6: si llega `proceso` en query params (deep-link desde Action
      // Center), se auto-selecciona ese; si no, fallback al PE.21 por defecto.
      const target = initialProcesoCodigo
        ? procesos.find((p) => p.codigo === initialProcesoCodigo)
        : procesos.find((p) => p.codigo === 'PE.21');
      if (target) {
        setSelectedProceso(target);
      }
    },
    [initialProcesoCodigo],
  );

  const handleSelectDoc = useCallback((doc: DocumentoResumen) => {
    setSelectedDocumento(doc);
    setDocEditing(false);
  }, []);

  // 0661.8: click en un pendiente del sidebar (En revision / En aprobacion)
  // debe abrir el documento DENTRO del panel de caracterizacion en modo
  // Visor, NO sacar al usuario via `navigate('/documento/...')`. Resuelve
  // el Proceso por `doc.proceso.codigo` desde la cache local.
  const handleNavigateDoc = useCallback(
    (doc: DocumentoResumen) => {
      const codigoProc = doc.proceso?.codigo;
      const proc = codigoProc ? procesosList.find((p) => p.codigo === codigoProc) : undefined;
      if (proc && proc.id !== selectedProceso?.id) {
        setSelectedProceso(proc);
      }
      setSelectedDocumento(doc);
      setDocEditing(false);
      setFichaMode('hidden');
    },
    [procesosList, selectedProceso?.id],
  );

  // 0661.6: auto-seleccion de documento por query param `doc=<codigo>` cuando
  // ya hay proceso seleccionado por el efecto anterior. Solo se ejecuta una
  // vez (autoSelectedDocRef) para no interferir con clicks posteriores.
  useEffect(() => {
    if (autoSelectedDocRef.current) return;
    if (!initialDocCodigo || !selectedProceso) return;
    autoSelectedDocRef.current = true;
    fetchDocumentos({ procesoId: selectedProceso.id, porPagina: '100' })
      .then((res) => {
        const doc = res.datos.find((d) => d.codigo === initialDocCodigo);
        if (doc) {
          setSelectedDocumento(doc);
          setDocEditing(false);
        }
      })
      .catch(() => {
        /* silencioso — fallback a no auto-seleccionar */
      });
  }, [initialDocCodigo, selectedProceso]);

  // Panel state is passed to DocumentPanel which handles its own header

  const cardStyle: React.CSSProperties = {
    background: '#fff',
    border: '1px solid #e8e8e8',
    borderRadius: 12,
    boxShadow: '0 1px 3px rgba(0,0,0,0.08)',
    overflow: 'hidden',
    display: 'flex',
    flexDirection: 'column',
  };
  const cardHeaderStyle: React.CSSProperties = {
    display: 'flex',
    alignItems: 'center',
    gap: 8,
    padding: '0 14px',
    height: 40,
    minHeight: 40,
    flexShrink: 0,
    borderBottom: '1px solid #e8e8e8',
    fontSize: 13,
  };

  return (
    <div
      style={{
        display: 'flex',
        flexDirection: 'column',
        height: '100%',
        width: '100%',
        backgroundColor: C.pageBg,
        overflow: 'hidden',
        fontFamily: 'inherit',
      }}
    >
      {/* 3 CARDS layout */}
      <div
        style={{
          flex: 1,
          display: 'flex',
          gap: 8,
          padding: 8,
          overflow: 'hidden',
          alignItems: 'stretch',
        }}
      >
        {/* CARD 1 - Tree */}
        <FichaTreeSidebar
          selectedCodigo={selectedProceso?.codigo ?? null}
          onSelectProceso={handleSelectProceso}
          onNavigateDoc={handleNavigateDoc}
          onProcesosLoaded={handleProcesosLoaded}
          viewMode={fichaMode}
          onViewModeChange={setFichaMode}
        />

        {!selectedProceso ? (
          <div
            style={{
              flex: 1,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              ...cardStyle,
            }}
          >
            <div style={{ textAlign: 'center', color: 'rgba(0,0,0,0.38)' }}>
              <Ic d={ICON_PATHS.doc} s={48} c="rgba(0,0,0,0.15)" />
              <div style={{ fontSize: 16, fontWeight: 500, marginTop: 16 }}>
                Selecciona un proceso del árbol
              </div>
              <div style={{ fontSize: 14, marginTop: 8 }}>para ver su ficha de caracterización</div>
            </div>
          </div>
        ) : (
          <>
            {/* CARD 2 - Ficha (visible in full + docs modes) */}
            {(fichaMode === 'full' || fichaMode === 'docs') && (
              <div
                style={
                  fichaMode === 'docs'
                    ? { width: 600, minWidth: 500, maxWidth: 600, flexShrink: 0, ...cardStyle }
                    : { flex: 2, ...cardStyle }
                }
              >
                {/* Card header */}
                <div style={cardHeaderStyle}>
                  <span
                    style={{
                      fontSize: 11,
                      fontWeight: 600,
                      color: C.azul2,
                      fontFamily: 'ui-monospace, "Cascadia Code", monospace',
                      whiteSpace: 'nowrap',
                    }}
                  >
                    {selectedProceso.codigo}.FC.01
                  </span>
                  <span
                    style={{
                      fontSize: 12,
                      fontWeight: 500,
                      color: C.text,
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      whiteSpace: 'nowrap',
                      minWidth: 0,
                    }}
                  >
                    {selectedProceso.nombre}
                  </span>
                  <StatusBadge status="PUBLICADO" small />
                  <div style={{ flex: 1 }} />
                  {fichaEditing ? (
                    <>
                      <button onClick={() => setFichaEditing(false)} style={hdrBtn}>
                        <Ic d={ICON_PATHS.save} s={13} c={C.azul2} /> {t.common.guardar}
                      </button>
                      <button onClick={() => setFichaEditing(false)} style={hdrBtn}>
                        {t.common.cancelar}
                      </button>
                    </>
                  ) : (
                    <>
                      <button onClick={() => setFichaEditing(true)} style={hdrBtn}>
                        <Ic d={ICON_PATHS.edit} s={13} c={C.textSec} /> {t.common.editar}
                      </button>
                      <button onClick={handleExportFicha} style={hdrBtn}>
                        <Ic d={ICON_PATHS.download} s={13} c={C.textSec} /> PDF
                      </button>
                    </>
                  )}
                </div>
                {/* Card content */}
                {fichaMode === 'docs' ? (
                  <div style={{ flex: 1, overflow: 'auto' }}>
                    <ProcessSheetPanel
                      proceso={selectedProceso}
                      onSelectDocumento={handleSelectDoc}
                      twoColumnLayout={false}
                      isEditing={false}
                      onToggleEdit={() => {}}
                      showOnlyDocs
                    />
                  </div>
                ) : (
                  <div ref={fichaRef} style={{ flex: 1, overflow: 'auto' }}>
                    <ProcessSheetPanel
                      proceso={selectedProceso}
                      onSelectDocumento={handleSelectDoc}
                      twoColumnLayout
                      isEditing={fichaEditing}
                      onToggleEdit={() => setFichaEditing((e) => !e)}
                    />
                  </div>
                )}
              </div>
            )}

            {/* CARD 3 - Visor/Flow (visible in full, docs, hidden/visor, flow modes) */}
            {(fichaMode === 'full' ||
              fichaMode === 'docs' ||
              fichaMode === 'hidden' ||
              fichaMode === 'flow') && (
              <div style={{ flex: 1, ...cardStyle }}>
                {fichaMode === 'flow' ? (
                  <div
                    style={{
                      flex: 1,
                      overflow: 'hidden',
                      display: 'flex',
                      flexDirection: 'column',
                    }}
                  >
                    <FlowErrorBoundary>
                      <FlowEditor
                        key={selectedProceso.id}
                        procesoId={selectedProceso.id}
                        procesoCodigo={selectedProceso.codigo}
                        procesoNombre={selectedProceso.nombre}
                      />
                    </FlowErrorBoundary>
                  </div>
                ) : (
                  <DocumentPanel
                    documento={selectedDocumento}
                    onExpandFullscreen={
                      selectedDocumento
                        ? () => navigate(`/documento/${selectedDocumento.codigo}`)
                        : undefined
                    }
                    editingFromParent={docEditing}
                    onEditingChange={setDocEditing}
                    onExportRef={docExportRef}
                    onStateChange={setPanelState}
                    useContentPages
                  />
                )}
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
}

export default FichaCaracterizacion;
